if (!customElements.get('product-inventory')) {
  class ProductInventory extends HTMLElement {
    constructor() {
      super();
      window.initLazyScript(this, this.initLazySection.bind(this));
    }

    initLazySection() {
      this.threshold = parseInt(this.dataset.threshold, 10);
      this.productInventory = this.querySelector('.product-inventory');
      this.inventoryNotice = this.querySelector('.product-inventory__status');
      this.variantInventory = this.getVariantInventory();

      this.closest('.js-product').addEventListener('on:variant:change', this.handleVariantChange.bind(this));
    }

    /**
     * Gets the inventory data for all product variants
     * @returns {?object}
     */
    getVariantInventory() {
      const dataEl = this.querySelector('[type="application/json"]');
      return this.variantInventory || JSON.parse(dataEl.textContent);
    }

    /**
     * Handles a 'change' event on the variant picker element
     * @param {object} evt - Event object
     */
    handleVariantChange(evt) {
      this.updateInventory(
        evt.detail.variant
          ? this.variantInventory.find((v) => v.id === evt.detail.variant.id)
          : null
      );
    }

    /**
     *
     * Updates the inventory notice
     * @param {?object} inventory - the inventory data
     */
    updateInventory(inventory) {
      if (!inventory) {
        this.productInventory.hidden = true;
        return;
      }

      const count = inventory.inventory_quantity;
      const showCount = this.dataset.showInventoryCount === 'always'
        || (
          this.dataset.showInventoryCount === 'low'
          && count <= this.threshold
        );

      let notice = null;
      if (showCount) {
        if (count <= this.threshold) {
          notice = this.dataset.textXLeftLow.replace('[QTY]', count);
        } else {
          notice = this.dataset.textXLeftOk.replace('[QTY]', count);
        }
      } else {
        // eslint-disable-next-line no-lonely-if
        if (count <= this.threshold) {
          notice = this.dataset.textLow;
        } else {
          notice = this.dataset.textOk;
        }
      }

      this.productInventory.classList.toggle('product-inventory--low', count <= this.threshold);
      this.productInventory.classList.toggle('product-inventory--ok', count > this.threshold);
      this.inventoryNotice.innerText = notice;
      this.productInventory.hidden = false;
    }
  }

  customElements.define('product-inventory', ProductInventory);
}

if (!customElements.get('quantity-wrapper')) {
  class QuantityWrapper extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', this.handleClick.bind(this));
    }

    handleClick(evt) {
      const btn = evt.target.closest('[data-quantity]');
      if (btn) {
        evt.preventDefault();
        const input = this.querySelector('[name="quantity"]');
        let change = btn.dataset.quantity === 'up' ? 1 : -1;
        if (input.step) {
          change *= parseInt(input.step, 10);
        }
        input.value = Math.max(1, parseInt(input.value, 10) + change);
        input.dispatchEvent(
          new CustomEvent('change', { bubbles: true, cancelable: false })
        );
      }
    }
  }

  customElements.define('quantity-wrapper', QuantityWrapper);
}

if (!customElements.get('variant-content')) {
  class VariantContent extends HTMLElement {
    constructor() {
      super();
      if (this.childElementCount > 0) {
        window.initLazyScript(this, this.initLazySection.bind(this));
      }
    }

    initLazySection() {
      this.closest('.js-product').addEventListener('on:variant:change', this.handleVariantChange.bind(this));
    }

    /**
     * Handles a 'change' event on the variant picker element
     * @param {object} evt - Event object
     */
    handleVariantChange(evt) {
      [...this.childNodes].filter((el) => el.tagName !== 'SCRIPT').forEach((el) => el.remove());
      this.querySelectorAll(':not(script)').forEach((el) => el.remove());
      const contentToShow = this.querySelector(`[data-variant="${evt.detail.variant ? evt.detail.variant.id : ''}"]`);
      if (contentToShow) {
        this.insertAdjacentHTML('beforeend', contentToShow.innerHTML);
      }
    }
  }

  customElements.define('variant-content', VariantContent);
}

if (!customElements.get('product-form')) {
  const ProductForm = class extends HTMLElement {
    constructor() {
      super();

      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        const idInput = this.form.querySelector('[name="id"]');
        idInput.disabled = false;
        idInput.required = true;

        if (theme.settings.afterAddToCart !== 'no-js') {
          this.submitBtn = this.querySelector('[name="add"]');
          this.form.addEventListener('submit', this.handleSubmit.bind(this));
        }
      }
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      evt.preventDefault();
      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      this.setErrorMsgState();

      const formValid = this.validate();
      if (!formValid) return;

      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');

      const formData = new FormData(this.form);

      try {
        liquidAjaxCart.add(formData, {
          firstCallback: (requestState) => {
            const response = requestState.responseData;
            if (!response.ok) throw new Error(response.status);
            const data = response.body;
            
            const cartDrawer = document.querySelector('cart-drawer');
            if (cartDrawer) {
              cartDrawer.open();
            }

            this.dispatchEvent(new CustomEvent('on:cart:add', {
              bubbles: true,
              detail: {
                variantId: data.variant_id,
                extra: data
              }
            }));

            this.submitBtn.classList.add('is-success');
            this.submitBtn.removeAttribute('aria-disabled');
            setTimeout(() => {
              this.submitBtn.classList.remove('is-loading');
              this.submitBtn.classList.remove('is-success');
            }, 2000);
          },
          lastCallback: async () => {
            if (!document.querySelector('.cart-link__count')) {
              try {
                const sectionID = document.querySelector('page-header').dataset.sectionId;
                const response = await fetch(`/?section_id=${sectionID}`);
                const data = await response.text();

                document.getElementById(`shopify-section-${sectionID}`).innerHTML = data;
              } catch (error) {
                console.error('Error updating cart link count', error)
              }
            }
          }
        });
      } catch (error) {
        console.log(error);

        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg.textContent
          }
        }));

        this.submitBtn.classList.remove('is-loading');
        this.submitBtn.classList.remove('is-success');
        this.submitBtn.removeAttribute('aria-disabled');
      }
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) this.errorMsg.textContent = error;
    }

    /**
     * Validate form & show errors.
     * @returns {boolean} - Is form valid.
     */
    validate() {
      let isValid = true;
      this.querySelectorAll('variant-picker .option-selector').forEach((el) => {
        if (el.dataset.selectorType === 'listed') {
          const input = el.querySelector('input');
          const inputIsValid = input.reportValidity();
          if (!inputIsValid) {
            isValid = false;
          }
        } else {
          // Clear prior errors
          el.querySelectorAll('.label__prefix').forEach((label) => label.remove());
          el.querySelectorAll('.label--contains-error').forEach((label) => label.classList.remove('label--contains-error'));

          const selectedOption = el.querySelector('.custom-select .js-option[aria-selected="true"]');
          if (!selectedOption) {
            const label = el.querySelector('.label');
            label.setAttribute('aria-live', 'polite');
            label.classList.add('label--contains-error');
            const labelPrefix = document.createElement('span');
            labelPrefix.innerText = `${theme.strings.productsProductChooseA} `;
            labelPrefix.className = 'label__prefix';
            label.prepend(labelPrefix);
            theme.scrollToRevealElement(el);
            isValid = false;
          }
        }
      });

      return isValid;
    }
  };

  window.customElements.define('product-form', ProductForm);
}

if (!customElements.get('buy-buttons')) {
  class BuyButtons extends HTMLElement {
    constructor() {
      super();
      window.initLazyScript(this, this.initLazySection.bind(this));
    }

    initLazySection() {
      this.dynamicPaymentButtonTemplate = this.querySelector('.dynamic-payment-button-template');

      if (this.dynamicPaymentButtonTemplate) {
        this.variantIdInput = this.querySelector('[name="id"]');

        if (this.variantIdInput.value) {
          this.tryInitDynamicPaymentButton();
        } else {
          this.boundTryInitDynamicPaymentButtonOnChange = this.tryInitDynamicPaymentButton.bind(this);
          this.variantIdInput.addEventListener('change', this.boundTryInitDynamicPaymentButtonOnChange);
        }
      }
    }

    /**
     * Initialise dynamic payment button, if variant exists
     */
    tryInitDynamicPaymentButton() {
      if (this.variantIdInput.value) {
        if (this.boundTryInitDynamicPaymentButtonOnChange) {
          this.variantIdInput.removeEventListener('change', this.boundTryInitDynamicPaymentButtonOnChange);
        }

        this.dynamicPaymentButtonTemplate.insertAdjacentHTML('afterend', this.dynamicPaymentButtonTemplate.innerHTML);
        this.dynamicPaymentButtonTemplate.remove();

        if (Shopify.PaymentButton) {
          Shopify.PaymentButton.init();
        }
      }
    }
  }

  customElements.define('buy-buttons', BuyButtons);
}